import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccountService } from '../../../../../services/account.service';

@Component({
  selector: 'app-advancepay-dialog',
  templateUrl: './advancepay-dialog.component.html',
  styleUrls: ['./advancepay-dialog.component.css']
})
export class AdvancepayDialogComponent implements OnInit {
  api_success: string;
  api_error: any;
  disableConfirmbtn = false;
  accountId: number;
  paymentProfiles: any[] = [];
  selectedJaldeeBank = true;
  advPayBankType = 'SP_DEFAULT';
  advancePaymentProfileId: string;
  advPayBankTypes = [
    { value: 'JALDEE_BANK', label: 'Jaldee Bank Profile' },
    { value: 'SP_DEFAULT', label: 'SP Default Profile' },
    { value: 'SERVICE_SPECIFIC', label: 'Service Specific Profile' },
    { value: 'SP_SPECIFIC_PROFILE', label: 'Own Bank Specific Profile' }
  ];

  constructor(
    private dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private accountService: AccountService
  ) {}

  ngOnInit() {
    this.accountId = this.data.accountId;
    this.loadInitialData();
  }

  loadInitialData() {
    this.accountService.getPayBankDetails(this.accountId)
      .subscribe((payBankDetails: any) => {
        this.setInitialSelection(payBankDetails || {});
        this.loadPaymentProfiles();
      }, () => {
        this.setInitialSelection({});
        this.loadPaymentProfiles();
      });
  }

  setInitialSelection(payBankDetails: any) {
    const hasSavedSelection = !!payBankDetails.advancePaymentProfileId;
    this.selectedJaldeeBank = hasSavedSelection && typeof payBankDetails.jaldeeBank === 'boolean' ? payBankDetails.jaldeeBank : true;
    this.advPayBankType = payBankDetails.advPayBankType || 'JALDEE_BANK';
    this.advancePaymentProfileId = payBankDetails.advancePaymentProfileId || '';
  }

  loadPaymentProfiles() {
    this.accountService.getPaymentProfiles(this.accountId)
      .subscribe((profiles: any) => {
        this.paymentProfiles = Array.isArray(profiles) ? profiles : [];
        this.syncSelectedProfileWithBank();
      }, (error) => {
        this.api_error = error.error;
      });
  }

  onBankSelectionChange() {
    this.advPayBankType = this.selectedJaldeeBank ? 'JALDEE_BANK' : 'SP_DEFAULT';
    this.syncSelectedProfileWithBank(true);
  }

  onAdvanceBankTypeChange() {
    if (this.shouldShowPaymentProfile()) {
      this.syncSelectedProfileWithBank(true);
    }
  }

  canSelectAdvanceBankType(): boolean {
    return !this.selectedJaldeeBank;
  }

  canSelectAdvanceBankTypeOption(typeValue: string): boolean {
    if (this.selectedJaldeeBank) {
      return typeValue === 'JALDEE_BANK';
    }
    return typeValue !== 'JALDEE_BANK';
  }

  canSelectProfile(profile: any): boolean {
    return this.isProfileForSelectedBank(profile);
  }

  shouldShowPaymentProfile(): boolean {
    return this.advPayBankType === 'JALDEE_BANK' || this.advPayBankType === 'SP_SPECIFIC_PROFILE';
  }

  setAdvancePayment() {
    if (this.shouldShowPaymentProfile() && !this.advancePaymentProfileId) {
      this.api_error = 'Select a payment profile';
      return;
    }

    this.api_error = '';
    this.api_success = '';
    this.disableConfirmbtn = true;

    const payload = {
      jaldeeBank: this.selectedJaldeeBank,
      advPayBankType: this.advPayBankType,
      advancePaymentProfileId: this.advancePaymentProfileId
    };

    this.accountService.updateAdvancePayBank(this.accountId, payload)
      .subscribe(() => {
        this.accountService.getPayBankDetails(this.accountId)
          .subscribe(() => {
            this.api_success = 'Updated successfully';
            setTimeout(() => {
              this.dialogRef.close('reloadlist');
            }, 1500);
          }, (error) => {
            this.api_error = error.error;
            this.disableConfirmbtn = false;
          });
      }, (error) => {
        this.api_error = error.error;
        this.disableConfirmbtn = false;
      });
  }

  getProfileId(profile: any): string {
    return profile.defaultPaymentProfileId || profile.paymentProfileId || profile.profileId || profile.id || profile.bankId || '';
  }

  getProfileLabel(profile: any): string {
    return profile.displayName || profile.profileName || profile.bankName || profile.accountHolderName || this.getProfileId(profile);
  }

  getJaldeeBankValue(profile: any): boolean {
    return !!(profile.jaldeeBank ?? profile.isJaldeeBank);
  }

  private syncSelectedProfileWithBank(forceSelection = false) {
    if (!this.paymentProfiles.length) {
      this.advancePaymentProfileId = '';
      return;
    }

    const selectedProfile = this.paymentProfiles.find(profile => this.getProfileId(profile) === this.advancePaymentProfileId);
    const selectedMatchesBank = selectedProfile && this.isProfileForSelectedBank(selectedProfile);
    if (!forceSelection && selectedMatchesBank) {
      return;
    }

    const matchingProfile = this.getPreferredProfileForSelectedBank()
      || this.paymentProfiles.find(profile => this.canSelectProfile(profile));
    this.advancePaymentProfileId = this.getProfileId(matchingProfile || this.paymentProfiles[0]);
  }

  private getPreferredProfileForSelectedBank() {
    const preferredNames = this.selectedJaldeeBank
      ? ['JALDEE BANK PAYMENT PROFILE', 'JALDEE BANK PROFILE']
      : ['WELLNESS PAYMENT PROFILE', 'WELL NESS BANK PROFILE'];
    return this.paymentProfiles.find(profile => preferredNames.includes(this.getProfileLabel(profile).toUpperCase()));
  }

  private isProfileForSelectedBank(profile: any): boolean {
    return this.isJaldeeBankProfile(profile) === this.selectedJaldeeBank;
  }

  private isJaldeeBankProfile(profile: any): boolean {
    const profileName = this.getProfileLabel(profile).toUpperCase();
    if (profileName === 'JALDEE BANK PAYMENT PROFILE' || profileName === 'JALDEE BANK PROFILE') {
      return true;
    }
    if (profileName === 'WELLNESS PAYMENT PROFILE' || profileName === 'WELL NESS BANK PROFILE') {
      return false;
    }
    return this.getJaldeeBankValue(profile);
  }
}
